import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRowsPerPagePref, setRowsPerPagePref } from '@/utils/tablePreferences';
import type { CheckedState } from '@radix-ui/react-checkbox';

export type SortField = 'name' | 'type' | 'total' | null;

type MinimalPortfolio = {
  id: string;
  name?: string | null;
  portfolio_type?: string | null;
  portfolio_size?: number | null;
};

type BulkSelectionLite = {
  isSelected: (id: string) => boolean;
};

export function usePortfolioTableState<T extends MinimalPortfolio>(
  portfolios: T[],
  bulkSelection: BulkSelectionLite,
  getTypeLabel: (t?: string | null) => string
) {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPagePref('portfolios', 25));
  const [pageInputValue, setPageInputValue] = useState('1');

  // Sorting state
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedPortfolios = useMemo<T[]>(() => {
    if (!sortField) return portfolios;
    const copy = [...(portfolios || [])] as T[];
    copy.sort((a, b) => {
      if (sortField === 'name') {
        const an = (a.name || '').toLowerCase();
        const bn = (b.name || '').toLowerCase();
        return an.localeCompare(bn);
      }
      if (sortField === 'type') {
        const at = getTypeLabel(a.portfolio_type).toLowerCase();
        const bt = getTypeLabel(b.portfolio_type).toLowerCase();
        return at.localeCompare(bt);
      }
      // total (sort by displayed value: portfolio_size)
      const atot = Number(a.portfolio_size || 0);
      const btot = Number(b.portfolio_size || 0);
      return atot - btot;
    });
    return sortDir === 'desc' ? copy.reverse() : copy;
  }, [portfolios, sortField, sortDir, getTypeLabel]);

  const totalCount = sortedPortfolios?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const pageIndex = Math.min(Math.max(1, currentPage), totalPages) - 1;
  const pageStart = pageIndex * rowsPerPage;
  const pageData = useMemo(
    () => sortedPortfolios.slice(pageStart, pageStart + rowsPerPage),
    [sortedPortfolios, pageStart, rowsPerPage]
  );

  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      setPageInputValue(String(page));
    },
    [totalPages]
  );

  const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value);
  }, []);

  const handlePageInputBlur = useCallback(() => {
    const newPage = Number(pageInputValue);
    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
      handlePageChange(newPage);
    } else {
      setPageInputValue(String(currentPage));
    }
  }, [pageInputValue, totalPages, handlePageChange, currentPage]);

  const handleRowsPerPageChange = useCallback((value: string) => {
    const n = Number(value);
    setRowsPerPage(n);
    setCurrentPage(1);
    setPageInputValue('1');
    setRowsPerPagePref('portfolios', n);
  }, []);

  const handleSortClick = useCallback(
    (field: Exclude<SortField, null>) => {
      if (sortField === field) {
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('asc');
      }
    },
    [sortField]
  );

  const headerChecked: CheckedState = useMemo(() => {
    const anySelected = pageData.some((p) => bulkSelection.isSelected(p.id));
    const allSelected = pageData.length > 0 && pageData.every((p) => bulkSelection.isSelected(p.id));
    return allSelected ? true : anySelected ? 'indeterminate' : false;
  }, [pageData, bulkSelection]);

  return {
    // data
    pageData,
    totalCount,
    totalPages,

    // pagination
    currentPage,
    rowsPerPage,
    pageInputValue,
    handlePageChange,
    handlePageInputChange,
    handlePageInputBlur,
    handleRowsPerPageChange,

    // sorting
    sortField,
    sortDir,
    handleSortClick,

    // selection
    headerChecked,
  };
}
